/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * AI-assisted recipe creation: generate, scan, import.
 *
 * All three produce a DRAFT that the user reviews before it is saved. That
 * review step is the difference between a library you trust and a library full
 * of plausible nonsense — so none of these screens can write a recipe directly.
 */

import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";

import { AiApi, CatalogApi, SettingsApi } from "../../api/endpoints.ts";
import { Banner, ErrorBox, Loading, PageHeader } from "../../components/ui.tsx";
import { Icon } from "../../components/Icon.tsx";
import { usePrefs } from "../../i18n/index.tsx";
import type { RecipeDraft } from "../../api/types.ts";

/** Back to the library, on every AI screen. None of them had one. */
function BackToRecipes() {
  return (
    <Link to="/recipes"
          className="mb-2 inline-flex items-center gap-1 text-sm text-ink-3 hover:text-ink">
      <Icon name="chevronLeft" className="h-4 w-4" /> Recipes
    </Link>
  );
}

/** Shared preview of a draft, with a hand-off to the editable review screen. */
function DraftReview({ draft, sourceUrl, lowConfidence, scanPhoto, imageUrl }: {
  draft: RecipeDraft; sourceUrl?: string; lowConfidence?: string[];
  /** A page photo from the scan flow, carried client-side — see migration 010. */
  scanPhoto?: string;
  /** A picture found on the imported page; the server fetches it on save. */
  imageUrl?: string | null;
}) {
  const navigate = useNavigate();
  const [usePhoto, setUsePhoto] = useState(true);

  // Hands the draft to the EDITABLE review screen rather than writing it
  // straight to the library. This used to save as-is, which meant a misread
  // quantity could only be caught after it was already a recipe you cook from —
  // and nothing on the web could edit it afterwards either.
  const review = () => navigate("/recipes/new", {
    state: {
      draft,
      source: sourceUrl ? "url" : "ai",
      sourceUrl,
      lowConfidence,
      scanPhoto: usePhoto ? scanPhoto : undefined,
      imageUrl: usePhoto ? imageUrl ?? undefined : undefined,
    },
  });

  const weak = draft.ingredients.filter((i) => i.matchConfidence < 0.6);

  return (
    <div className="space-y-4">
      <div className="card p-4">
        <h2 className="type-title">{draft.title}</h2>
        {draft.description && <p className="mt-1 text-sm text-ink-2">{draft.description}</p>}
        <p className="mt-2 text-sm text-ink-3">
          {draft.servingsBase} servings
          {draft.cookMinutes ? ` · ${(draft.prepMinutes ?? 0) + draft.cookMinutes} min` : ""}
        </p>
      </div>

      {lowConfidence && lowConfidence.length > 0 && (
        <Banner tone="warn">
          {lowConfidence.length} field{lowConfidence.length === 1 ? " was" : "s were"} hard
          to read in the source. Check the quantities before saving.
        </Banner>
      )}
      {weak.length > 0 && (
        <Banner tone="warn">
          {weak.length} ingredient{weak.length === 1 ? "" : "s"} couldn't be matched to the
          food catalog. They'll still work, but won't combine on the shopping list.
        </Banner>
      )}

      <section className="card divide-y divide-rule-soft">
        {draft.ingredients.map((i, idx) => (
          <div key={idx} className="flex items-baseline justify-between gap-3 px-4 py-2 text-sm">
            <span>
              {i.quantity !== null && <strong>{i.quantity}{i.unit ? ` ${i.unit}` : ""} </strong>}
              {i.displayName}
              {i.prepNote && <span className="text-ink-3">, {i.prepNote}</span>}
            </span>
            <span className="shrink-0 text-xs text-ink-3">
              {i.scaleMode !== "linear" && <span className="chip mr-1">{i.scaleMode}</span>}
              {i.matchConfidence < 0.6 && <span className="text-warn">unmatched</span>}
            </span>
          </div>
        ))}
      </section>

      <ol className="space-y-2">
        {draft.steps.map((s, idx) => (
          <li key={idx} className="flex gap-3 text-sm">
            <span className="label-mono pt-0.5">{idx + 1}</span>
            <span>{s.text}</span>
          </li>
        ))}
      </ol>

      {(scanPhoto || imageUrl) && (
        <label className="card flex items-start gap-3 p-4">
          <input type="checkbox" className="mt-1" checked={usePhoto}
                 onChange={(e) => setUsePhoto(e.target.checked)} />
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium text-ink">
              {scanPhoto ? "Use the page you photographed as the picture"
                         : "Use the photo from that page"}
            </span>
            {/* Said plainly because it is genuinely a compromise: a photo of a
                cookbook page is not a photo of the dish, and someone should be
                able to decline it without hunting for the option. */}
            <span className="type-meta mt-0.5 block">
              {scanPhoto
                ? "It's the book page rather than the finished dish — you can replace it later with your own."
                : "Taken from the recipe's own page. Stored with your copy, so it won't break if their site changes."}
            </span>
          </span>
          {scanPhoto && (
            <img src={scanPhoto} alt=""
                 className="h-16 w-16 shrink-0 rounded-lg border border-rule-soft object-cover" />
          )}
        </label>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button className="btn-primary" onClick={review}>Review and save</button>
        <span className="text-xs text-ink-3">
          Nothing is saved until you've checked it over.
        </span>
      </div>
    </div>
  );
}

// ── generate ─────────────────────────────────────────────────────────────────

/** Structured moods, so "what are you feeling?" is a tap rather than an essay. */
const MOODS = [
  "comforting", "light and fresh", "quick and lazy", "spicy",
  "hearty", "healthy", "something special", "one pan",
  "batch cook", "use up what's in the fridge",
];

/**
 * Generate.
 *
 * ── TWO TIERS, ZERO CONTROLS REMOVED ────────────────────────────────────────
 * This screen had fourteen inputs visible at once — prompt, ten mood chips,
 * meal, servings, max minutes, cuisine, must-use, an allergen grid, a diet
 * grid, dislikes and avoid-recent — which is a lot to meet before you have
 * seen the feature do anything, and most of it has a sane default.
 *
 * So: the three that shape the answer most (what you fancy, mood, servings) are
 * open; the other nine live under "More options". EVERY ONE OF THEM IS STILL
 * HERE and still sent. The collapse is presentational — `generate.mutate()`
 * builds precisely the same body it always did, so a power user loses nothing
 * and a first-time user is not asked to have an opinion about `scaleMode`
 * before they've typed a word.
 *
 * The section auto-opens if any advanced field is non-default, which is what
 * makes it safe to collapse: state you can't see can't be forgotten about.
 *
 * ── AND IT NOW READS ?title= ────────────────────────────────────────────────
 * Home's "Make this recipe" links have always pointed here with the idea's
 * title in the query string, and this screen has always ignored it — so the
 * one-click path from an AI suggestion to an AI recipe dropped its payload and
 * presented an empty box.
 */
export function GenerateRecipeView() {
  const { lang } = usePrefs();
  const [params] = useSearchParams();

  const [prompt, setPrompt] = useState(() => params.get("title") ?? "");
  const [servings, setServings] = useState(4);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [maxMinutes, setMaxMinutes] = useState<number | "">("");
  const [cuisine, setCuisine] = useState("");
  const [mealSlot, setMealSlot] = useState("dinner");
  const [moods, setMoods] = useState<string[]>([]);
  const [mustUse, setMustUse] = useState("");
  const [diets, setDiets] = useState<string[]>([]);
  const [avoid, setAvoid] = useState<string[]>([]);
  const [dislikes, setDislikes] = useState("");
  const [avoidRecent, setAvoidRecent] = useState(true);

  const catalog = useQuery({
    queryKey: ["diet-options", lang],
    queryFn: () => CatalogApi.dietOptions(lang),
    staleTime: 60 * 60 * 1000,
  });
  const settings = useQuery({ queryKey: ["settings"], queryFn: () => SettingsApi.get() });

  // The household's own allergens are applied SERVER-side whatever the client
  // sends — shown here so it is visible, not so it is optional.
  const householdAllergens = [
    ...new Set((settings.data?.diets ?? []).flatMap((d) => d.allergens)),
  ];
  const slots = settings.data?.slots ?? [];

  const toggle = (list: string[], v: string) =>
    list.includes(v) ? list.filter((x) => x !== v) : [...list, v];

  const generate = useMutation({
    mutationFn: () => AiApi.generate({
      prompt: prompt.trim() || undefined,
      servings,
      mealSlot,
      mood: moods.join(", ") || undefined,
      maxMinutes: maxMinutes || undefined,
      cuisine: cuisine.trim() || undefined,
      mustUse: mustUse.split(",").map((s) => s.trim()).filter(Boolean),
      dietTags: diets,
      allergens: avoid,
      dislikes: dislikes.split(",").map((s) => s.trim()).filter(Boolean),
      avoidRecent,
    }),
  });

  const allergenOptions = catalog.data?.allergens ?? [];
  const dietOptions = catalog.data?.diets ?? [];

  /**
   * How many advanced fields are set.
   *
   * Shown on the toggle, and used to force the section open on mount. A
   * collapsed panel hiding an active constraint is how someone ends up asking
   * why every recipe comes back vegan.
   */
  const advancedCount =
    (mealSlot !== "dinner" ? 1 : 0) + (maxMinutes !== "" ? 1 : 0)
    + (cuisine.trim() ? 1 : 0) + (mustUse.trim() ? 1 : 0)
    + diets.length + avoid.length + (dislikes.trim() ? 1 : 0)
    + (avoidRecent ? 0 : 1);
  const advancedOpen = showAdvanced || advancedCount > 0;

  return (
    <div className="max-w-2xl animate-fade-in space-y-4">
      <PageHeader
        title="Generate a recipe"
        subtitle="Tell it roughly what you want; it writes something you can actually cook"
      />

      {/* ── Tier one: the three that matter ──────────────────────────────── */}
      <div className="card space-y-4 p-5">
        <label className="block">
          <span className="text-sm font-medium">What are you after?</span>
          <textarea className="input mt-1" rows={2}
            placeholder="something warming with the squash we have"
            value={prompt} onChange={(e) => setPrompt(e.target.value)} />
        </label>

        <div>
          <div className="label-mono mb-1.5">How are you feeling?</div>
          <div className="flex flex-wrap gap-2">
            {MOODS.map((m) => (
              <button key={m} type="button" className="chip-toggle"
                      aria-pressed={moods.includes(m)}
                      onClick={() => setMoods((x) => toggle(x, m))}>
                {m}
              </button>
            ))}
          </div>
        </div>

        <label className="block max-w-[10rem]">
          <span className="text-sm">Servings</span>
          <input type="number" min={1} className="input mt-1" value={servings}
            onChange={(e) => setServings(Number(e.target.value) || 4)} />
        </label>

        {/* The safety statement stays in tier one, always. It is the single
            most reassuring fact about this screen, and burying reassurance
            inside a collapsed section defeats the point of having it. */}
        {householdAllergens.length > 0 && (
          <p className="rounded-lg border border-danger/30 bg-danger/5 p-3 text-xs text-ink-2">
            Always applied for your household:{" "}
            <strong className="text-danger">{householdAllergens.join(", ")}</strong>.
            {" "}Enforced in code after the model answers — a recipe containing one is
            rejected, not shown.
          </p>
        )}
      </div>

      {/* ── Tier two: everything else, all of it still here ──────────────── */}
      <div className="card overflow-hidden">
        <button
          type="button"
          onClick={() => setShowAdvanced((s) => !s)}
          aria-expanded={advancedOpen}
          className="flex w-full items-center justify-between gap-2 p-4 text-left transition hover:bg-paper-3"
        >
          <span className="text-sm font-medium">
            More options
            {advancedCount > 0 && (
              <span className="ml-2 chip border-accent/40 text-accent">{advancedCount} set</span>
            )}
          </span>
          <Icon name="chevronDown"
                className={`h-4 w-4 shrink-0 text-ink-3 transition ${advancedOpen ? "rotate-180" : ""}`} />
        </button>

        {advancedOpen && (
          <div className="space-y-4 border-t border-rule-soft p-5">
            <div className="grid gap-3 sm:grid-cols-3">
              <label className="block">
                <span className="text-sm">Meal</span>
                <select className="input mt-1" value={mealSlot}
                        onChange={(e) => setMealSlot(e.target.value)}>
                  {slots.length === 0 && <option value="dinner">Dinner</option>}
                  {slots.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-sm">Max minutes</span>
                <input type="number" min={5} className="input mt-1" value={maxMinutes}
                  placeholder="any"
                  onChange={(e) => setMaxMinutes(e.target.value ? Number(e.target.value) : "")} />
              </label>
              <label className="block">
                <span className="text-sm">Cuisine</span>
                <input className="input mt-1" placeholder="any"
                       value={cuisine} onChange={(e) => setCuisine(e.target.value)} />
              </label>
            </div>

            <label className="block">
              <span className="text-sm">Must use</span>
              <input className="input mt-1" placeholder="squash, half a tin of beans"
                     value={mustUse} onChange={(e) => setMustUse(e.target.value)} />
            </label>

            <div>
              <div className="label-mono mb-1.5">Avoid, for this recipe only</div>
              <div className="flex flex-wrap gap-2">
                {allergenOptions
                  .filter((o) => !householdAllergens.includes(o.key))
                  .map((o) => (
                    <button key={o.key} type="button" title={o.description}
                            aria-pressed={avoid.includes(o.key)}
                            onClick={() => setAvoid((x) => toggle(x, o.key))}
                            className={`chip transition ${
                              avoid.includes(o.key)
                                ? "border-danger bg-danger/15 text-danger"
                                : "hover:border-danger/40"}`}>
                      {o.label}
                    </button>
                  ))}
              </div>
              <input className="input mt-2" placeholder="Anything else to avoid, comma separated"
                     value={dislikes} onChange={(e) => setDislikes(e.target.value)} />
            </div>

            <div>
              <div className="label-mono mb-1.5">Follow a diet</div>
              <div className="flex flex-wrap gap-2">
                {dietOptions.map((o) => (
                  <button key={o.key} type="button" title={o.description}
                          className="chip-toggle" aria-pressed={diets.includes(o.key)}
                          onClick={() => setDiets((x) => toggle(x, o.key))}>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={avoidRecent}
                     onChange={(e) => setAvoidRecent(e.target.checked)} />
              Avoid what we've cooked lately
            </label>
          </div>
        )}
      </div>

      <button className="btn-primary w-full" onClick={() => generate.mutate()}
              disabled={generate.isPending}>
        <Icon name="sparkles" className="h-4 w-4" />
        {generate.isPending ? "Thinking…" : "Generate"}
      </button>

      {generate.isPending && <Loading label="Writing a recipe…" />}
      {generate.error && <ErrorBox message={(generate.error as Error).message} />}
      {generate.data && <DraftReview draft={generate.data.draft} />}
    </div>
  );
}

// ── scan ─────────────────────────────────────────────────────────────────────

export function ScanRecipeView() {
  const [scanId, setScanId] = useState<string | null>(null);
  // Kept purely so it can become the recipe's picture. Migration 010 does not
  // retain scan images server-side, so if the client drops it, it is gone.
  const [firstPage, setFirstPage] = useState<string | null>(null);
  const [status, setStatus] = useState<string>("");
  const [draft, setDraft] = useState<RecipeDraft | null>(null);
  const [raw, setRaw] = useState<string | null>(null);
  const [lowConfidence, setLowConfidence] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const delayRef = useRef(1000);

  const start = useMutation({
    mutationFn: (images: string[]) => AiApi.scan(images),
    onSuccess: (r) => { setScanId(r.scanId); setStatus("pending"); },
    onError: (e: Error) => setError(e.message),
  });

  // OCR plus structuring is 5–20s, so the server runs it async and we poll.
  useEffect(() => {
    if (!scanId || draft) return;
    const tick = async () => {
      try {
        const s = await AiApi.scanStatus(scanId);
        setStatus(s.status);
        setRaw(s.ocr_markdown);
        if (s.status === "parsed" && s.parsed_json) {
          setDraft(s.parsed_json);
          setLowConfidence(s.low_confidence_fields ?? []);
          return;
        }
        if (s.status === "failed") { setError(s.error ?? "The scan failed."); return; }
        // Backoff, not a fixed 1.5s. A tight interval sends ~40 requests a
        // minute for a twenty-second job, most of them learning nothing, and
        // it is what tripped the server's rate limit and turned a working scan
        // into a wall of 429s.
        delayRef.current = Math.min(delayRef.current + 500, 5000);
        pollRef.current = setTimeout(() => void tick(), delayRef.current);
      } catch (e) {
        setError((e as Error).message);
      }
    };
    void tick();
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
  }, [scanId, draft]);

  const onFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    const images = await Promise.all(
      [...files].slice(0, 8).map((f) => new Promise<string>((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result as string);
        r.onerror = () => rej(new Error("Couldn't read that file."));
        r.readAsDataURL(f);
      })));
    setFirstPage(images[0] ?? null);
    start.mutate(images);
  };

  return (
    <div className="max-w-2xl animate-fade-in space-y-4">
      <BackToRecipes />
      <PageHeader
        title="Scan a recipe"
        subtitle="Photograph a page — up to 8 for a spread. Nothing is saved until you review it."
      />

      {!scanId && (
        <label className="card flex cursor-pointer flex-col items-center gap-3 border-dashed p-10 text-center transition hover:border-accent">
          <input type="file" accept="image/*" multiple className="hidden"
            onChange={(e) => void onFiles(e.target.files)} />
          <span aria-hidden className="grid h-12 w-12 place-items-center rounded-xl bg-accent/10 text-accent">
            <Icon name="camera" className="h-6 w-6" />
          </span>
          <span className="text-sm font-medium text-ink">Choose photos or a PDF page</span>
          <span className="max-w-xs text-xs text-ink-3">
            Cookbooks, magazine clippings, handwritten cards. Anything the reader
            struggles with gets flagged for you to check rather than guessed at.
          </span>
        </label>
      )}

      {error && <ErrorBox message={error} />}
      {scanId && !draft && !error && (
        <Loading label={
          status === "pending" ? "Reading the page…"
            : status === "ocr_done" ? "Making sense of it…"
            : "Working…"} />
      )}

      {draft && (
        <div className="grid gap-4 lg:grid-cols-2">
          <DraftReview draft={draft} lowConfidence={lowConfidence}
                       scanPhoto={firstPage ?? undefined} />
          {raw && (
            <details className="card p-4 text-xs">
              <summary className="cursor-pointer text-sm">Raw text from the page</summary>
              <pre className="mt-2 whitespace-pre-wrap font-mono">{raw}</pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

// ── import ───────────────────────────────────────────────────────────────────

export function ImportRecipeView() {
  const [url, setUrl] = useState("");
  const importUrl = useMutation({ mutationFn: () => AiApi.importUrl(url) });

  return (
    <div className="max-w-2xl animate-fade-in space-y-4">
      <BackToRecipes />
      <PageHeader
        title="Paste a link"
        subtitle="From any recipe site. Most publish structured data we can read exactly — no AI needed."
      />
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); importUrl.mutate(); }}>
        <input className="input" type="url" placeholder="https://…" autoFocus
          aria-label="Recipe URL"
          value={url} onChange={(e) => setUrl(e.target.value)} />
        <button className="btn-primary shrink-0" disabled={!url || importUrl.isPending}>
          <Icon name="link" className="h-4 w-4" />
          Import
        </button>
      </form>

      {importUrl.isPending && <Loading label="Fetching the page…" />}
      {importUrl.error && <ErrorBox message={(importUrl.error as Error).message} />}
      {importUrl.data && (
        <>
          {/* Most recipe sites publish schema.org/Recipe, which is exact and
              needs no model at all. Say which path was taken. */}
          <Banner>
            {importUrl.data.via === "json-ld"
              ? "Read directly from the page's structured data — no AI needed."
              : "The page had no structured recipe data, so it was interpreted by AI. Check it."}
          </Banner>
          <DraftReview draft={importUrl.data.draft} sourceUrl={importUrl.data.sourceUrl}
                       imageUrl={importUrl.data.imageUrl} />
        </>
      )}
    </div>
  );
}
